import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useIsSeller } from "@/hooks/useIsSeller";
import { useTranslation } from "react-i18next";
import { openCookiePreferences } from "@/lib/cookieConsent";

const ScrollToTopLink = ({
  to,
  children,
  className
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) => {
  const navigate = useNavigate();
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(to);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>;
};
export const Footer = () => {
  const {
    t
  } = useTranslation('common');
  const navigate = useNavigate();
  const {
    isSeller
  } = useIsSeller();
  const footerLinks = {
    shop: [{
      label: t('footer.allCategories'),
      path: "/category"
    }, {
      label: t('navigation.newArrivals'),
      path: "/new-arrivals"
    }, {
      label: t('navigation.giftCards'),
      path: "/gift-cards"
    }],
    forSellers: [{
      label: t('navigation.becomeSeller'),
      path: "/become-a-seller"
    }, {
      label: t('footer.resources'),
      path: "/seller-resources"
    }, {
      label: t('navigation.community'),
      path: "/community"
    }, {
      label: t('footer.sellerAgreement'),
      path: "/seller-agreement"
    }],
    about: [{
      label: t('navigation.ourStory'),
      path: "/our-story"
    }, {
      label: t('navigation.press'),
      path: "/press"
    }, {
      label: t('navigation.contact'),
      path: "/contact"
    }]
  };
  return <footer className="bg-card border-t border-border">
      <div className="container mx-auto px-4 py-6 md:py-12">

        {/* Footer Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 md:gap-8 md:mb-12 text-center md:text-left">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="font-display font-bold text-2xl text-primary mb-2 md:mb-4 cursor-pointer" onClick={() => {
            navigate("/");
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }}>
              PERLLA
            </div>
            <p className="text-muted-foreground text-sm mb-2 md:mb-4">
              {t('footer.tagline')}
            </p>
            <div className="flex justify-center md:justify-start space-x-2 md:space-x-4">
              <Button variant="ghost" size="icon" aria-label="Visit our Facebook page">
                <Facebook className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Visit our Instagram page">
                <Instagram className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Visit our Twitter page">
                <Twitter className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="font-semibold text-foreground mb-2 md:mb-4">{t('footer.shop')}</h4>
            <ul className="space-y-1 md:space-y-2">
              {footerLinks.shop.map(link => <li key={link.path}>
                  <ScrollToTopLink to={link.path} className="text-muted-foreground hover:text-primary perlla-transition text-sm block">
                    {link.label}
                  </ScrollToTopLink>
                </li>)}
            </ul>
          </div>

          {/* For Sellers Links */}
          <div>
            <h4 className="font-semibold text-foreground mb-2 md:mb-4">{t('footer.forSellers')}</h4>
            <ul className="space-y-1 md:space-y-2">
              {footerLinks.forSellers.map(link => <li key={link.path}>
                  <ScrollToTopLink to={link.path} className="text-muted-foreground hover:text-primary perlla-transition text-sm block">
                    {link.label}
                  </ScrollToTopLink>
                </li>)}
            </ul>
          </div>

          {/* About Links */}
          <div>
            <h4 className="font-semibold text-foreground mb-2 md:mb-4">{t('footer.about')}</h4>
            <ul className="space-y-1 md:space-y-2">
              {footerLinks.about.map(link => <li key={link.path}>
                  <ScrollToTopLink to={link.path} className="text-muted-foreground hover:text-primary perlla-transition text-sm block">
                    {link.label}
                  </ScrollToTopLink>
                </li>)}
            </ul>
          </div>
        </div>

        <Separator className="mb-4 md:mb-8" />

        {/* Contact Info & Bottom Section */}
        <div className="flex flex-col lg:flex-row justify-between items-center md:gap-4 my-0 mx-0 gap-[8px]">
          <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span>hello@perlla.com</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              <span>+357 123 456 789</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{t('footer.location')}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 text-sm text-muted-foreground">
            <span>© 2025 PERLLA. {t('footer.allRightsReserved')}</span>
            <div className="flex flex-wrap justify-center gap-4">
              <ScrollToTopLink to="/privacy-policy" className="hover:text-primary perlla-transition">{t('footer.privacyPolicy')}</ScrollToTopLink>
              <ScrollToTopLink to="/terms-of-service" className="hover:text-primary perlla-transition">{t('footer.termsOfService')}</ScrollToTopLink>
              <button
                type="button"
                onClick={() => openCookiePreferences()}
                className="hover:text-primary perlla-transition"
              >
                {t('cookies:footer.link')}
              </button>
            </div>

          </div>
        </div>
      </div>
      {/* Extra padding for mobile bottom navigation */}
      <div className="h-16 md:hidden" />
    </footer>;
};